import {env} from '@matic/config/env';
import {API_TIMEOUTS, ERROR_MESSAGES} from '@matic/config/constants';
import logger from '@matic/lib/logging/logger';
import {
  extractResourceIds,
  shouldSkipApiTelemetry,
  summarizeResponseMetadata,
  trackProductEvent,
} from '@matic/lib/product-observability';
import {
  EnhancedApiError,
  ErrorCategory,
  createErrorContext,
} from './error-types';

/**
 * Request options, extended with an explicit timeout override.
 *
 * `timeoutMs: null` opts a request out of the default timeout entirely — for
 * calls that legitimately run longer than the default budget (e.g. Otto
 * Views' bounded LLM generation, 10-30s, which can exceed the default). A
 * plain number overrides the default/upload timeout with a specific value.
 * Prefer this over passing a raw `AbortSignal` to dodge the timeout — that
 * only "worked" as a side effect of `createTimedFetchOptions` calling
 * `.abort?.()` on a signal (not a controller), which silently no-ops. If that
 * bug is ever fixed, callers relying on the side effect would start getting
 * hard-timed-out with no explicit signal of intent; `timeoutMs: null` doesn't
 * depend on that bug either way.
 */
export type ApiRequestOptions = RequestInit & {
  timeoutMs?: number | null;
};

/**
 * Base API client
 *
 * This class provides a foundation for API clients with common
 * functionality like request handling, error processing, etc.
 */
export class BaseApiClient {
  /**
   * Clear local OIDC state via UserManager.removeUser().
   * AuthProvider will react to the storage event and update isAuthenticated,
   * which triggers the route guard redirect to login.
   */
  private static async forceSignOut(): Promise<void> {
    try {
      const {getUserManager} = await import('@matic/lib/auth/user-manager');
      await getUserManager()?.removeUser();
    } catch {
      // Best-effort — if removeUser fails, the 401 error still propagates.
    }
  }

  protected baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? env.API_URL;
  }

  /**
   * Prepare headers with Content-Type and auth tokens
   *
   * Protected to allow subclasses (like AuthenticatedApiClient) to override
   */
  protected async prepareHeaders(options: RequestInit): Promise<Headers> {
    const headers = new Headers(options.headers ?? {});
    if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    // Add auth token from OIDC session
    try {
      const {getAuthHeaders} = await import('@matic/lib/auth/oidc-token');
      const authHeaders = await getAuthHeaders();
      for (const [key, value] of Object.entries(authHeaders)) {
        headers.set(key, value);
      }
    } catch {
      // Auth headers unavailable — request proceeds without auth
    }

    return headers;
  }

  /**
   * Make an API request with automatic 401 retry logic
   *
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @returns Response data
   */
  protected async request<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const method = options.method ?? 'GET';
    const startedAt = performance.now();
    const shouldTrack = !shouldSkipApiTelemetry(endpoint);

    const headers = await this.prepareHeaders(options);

    const fetchOptions = this.createTimedFetchOptions(
      {...options, headers},
      method
    );

    try {
      const response = await fetch(url, fetchOptions);

      // Clear timeout if stored
      const timeoutId = (
        fetchOptions.signal as AbortSignal & {timeoutId?: NodeJS.Timeout}
      )?.timeoutId;
      if (timeoutId) clearTimeout(timeoutId);

      // Handle 401 Unauthorized - check if it's validation vs token expiration
      if (response.status === 401) {
        // Check if this is a credential validation error
        const validationCheck = await this.isValidationError(response);

        if (validationCheck.isValidation) {
          if (shouldTrack) {
            trackProductEvent('api.failed', {
              method,
              endpoint,
              status: response.status,
              latencyMs: Math.round(performance.now() - startedAt),
              resourceIds: extractResourceIds(endpoint),
              errorCategory: 'validation',
            });
          }

          throw new EnhancedApiError(
            validationCheck.message ?? 'Invalid credentials',
            401,
            {
              error: {
                code: validationCheck.code ?? 'invalid_credentials',
                message: validationCheck.message,
              },
            }
          );
        }

        // Genuine token expiration — attempt active refresh and retry
        return await this.handle401AndRetry(url, fetchOptions, endpoint);
      }

      // Handle other non-2xx responses
      if (!response.ok) {
        if (shouldTrack) {
          trackProductEvent('api.failed', {
            method,
            endpoint,
            status: response.status,
            latencyMs: Math.round(performance.now() - startedAt),
            resourceIds: extractResourceIds(endpoint),
            errorCategory: 'http',
          });
        }

        await this.handleBadResponseError(response, fetchOptions, endpoint);
      }

      // Handle empty responses
      if (response.status === 204) {
        if (shouldTrack) {
          trackProductEvent('api.completed', {
            method,
            endpoint,
            status: response.status,
            latencyMs: Math.round(performance.now() - startedAt),
            resourceIds: extractResourceIds(endpoint),
          });
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {} as T;
      }

      // Parse JSON response
      const data = (await response.json()) as T;
      if (shouldTrack) {
        trackProductEvent('api.completed', {
          method,
          endpoint,
          status: response.status,
          latencyMs: Math.round(performance.now() - startedAt),
          resourceIds: extractResourceIds(endpoint),
          response: summarizeResponseMetadata(data),
        });
      }

      return data;
    } catch (error) {
      if (shouldTrack && !(error instanceof EnhancedApiError)) {
        trackProductEvent('api.failed', {
          method,
          endpoint,
          status: 0,
          latencyMs: Math.round(performance.now() - startedAt),
          resourceIds: extractResourceIds(endpoint),
          errorCategory: 'network',
          error: error instanceof Error ? error.message : String(error),
        });
      }

      this.handleUnexpectedError(error, endpoint, method);
    }
  }

  /**
   * Make a GET request
   *
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @returns Response data
   */
  protected async get<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  /**
   * Make a POST request
   *
   * @param endpoint - API endpoint
   * @param data - Request data
   * @param options - Fetch options
   * @returns Response data
   */
  protected async post<T>(
    endpoint: string,
    data: unknown,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  /**
   * Make a PUT request
   *
   * @param endpoint - API endpoint
   * @param data - Request data
   * @param options - Fetch options
   * @returns Response data
   */
  protected async put<T>(
    endpoint: string,
    data: unknown,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  /**
   * Make a PATCH request
   *
   * @param endpoint - API endpoint
   * @param data - Request data
   * @param options - Fetch options
   * @returns Response data
   */
  protected async patch<T>(
    endpoint: string,
    data: unknown,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  /**
   * Make a DELETE request
   *
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @returns Response data
   */
  protected async delete<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }

  /**
   * Make a POST request that returns a Blob (for file conversions/downloads)
   *
   * @param endpoint - API endpoint
   * @param data - Request data (FormData or JSON)
   * @param options - Fetch options
   * @returns Blob response
   */
  protected async postBlob(
    endpoint: string,
    data: unknown,
    options: ApiRequestOptions = {}
  ): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;
    const isFormData = data instanceof FormData;

    // For FormData, don't set Content-Type - let browser set it with boundary
    const headers = await this.prepareHeaders({
      ...options,
      body: isFormData ? data : undefined, // Pass body so prepareHeaders knows it's FormData
    });

    // Remove Content-Type for FormData - browser must set it with multipart boundary
    if (isFormData) {
      headers.delete('Content-Type');
    }

    const fetchOptions = this.createTimedFetchOptions(
      {
        ...options,
        headers,
        body: isFormData ? data : JSON.stringify(data),
      },
      'POST'
    );

    try {
      const response = await fetch(url, fetchOptions);

      // Clear timeout if stored
      const timeoutId = (
        fetchOptions.signal as AbortSignal & {timeoutId?: NodeJS.Timeout}
      )?.timeoutId;
      if (timeoutId) clearTimeout(timeoutId);

      // Handle 401 Unauthorized with automatic retry
      if (response.status === 401) {
        return await this.handle401AndRetryBlob(url, fetchOptions, endpoint);
      }

      // Handle non-2xx responses
      if (!response.ok) {
        await this.handleBadResponseError(response, fetchOptions, endpoint);
      }

      // Return blob response
      return await response.blob();
    } catch (error) {
      this.handleUnexpectedError(error, endpoint, 'POST');
    }
  }

  /**
   * Make a GET request that returns a Blob (for file downloads)
   *
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @returns Blob response
   */
  protected async getBlob(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers = await this.prepareHeaders(options);

    const fetchOptions = this.createTimedFetchOptions(
      {...options, headers},
      'GET'
    );

    try {
      const response = await fetch(url, fetchOptions);

      // Clear timeout if stored
      const timeoutId = (
        fetchOptions.signal as AbortSignal & {timeoutId?: NodeJS.Timeout}
      )?.timeoutId;
      if (timeoutId) clearTimeout(timeoutId);

      // Handle 401 Unauthorized with automatic retry (same as request())
      if (response.status === 401) {
        return await this.handle401AndRetryBlob(url, fetchOptions, endpoint);
      }

      // Handle non-2xx responses
      if (!response.ok) {
        await this.handleBadResponseError(response, fetchOptions, endpoint);
      }

      // Return blob response
      return await response.blob();
    } catch (error) {
      this.handleUnexpectedError(error, endpoint, 'GET');
    }
  }

  /**
   * Authenticated fetch that mirrors `request()`'s 401 handling but returns the
   * raw `Response`, so streaming callers (Otto / Scribe / Lattice SSE) that read
   * `response.body` directly can share the same self-healing auth path.
   *
   * On a genuine token-expiry 401 it refreshes via `signinSilent()` and retries
   * once; if the retry still 401s it forces sign-out (→ route-guard redirect to
   * login). Validation 401s (wrong credentials) are returned unmodified for the
   * caller to surface.
   *
   * Without this, the SSE clients bypass `request()`'s refresh-and-retry and a
   * stale token leaves the user stuck on a dead-end "Session expired" message
   * with no recovery short of a manual re-login.
   */
  protected async fetchWithAuthRetry(
    url: string,
    init: RequestInit
  ): Promise<Response> {
    const response = await fetch(url, init);
    if (response.status !== 401) {
      return response;
    }

    // Wrong-credentials 401s aren't fixable by a token refresh — hand back as-is.
    const validationCheck = await this.isValidationError(response);
    if (validationCheck.isValidation) {
      return response;
    }

    // Genuine token expiry — refresh and retry once (throws + signs out if the
    // refresh itself fails, so the caller's catch surfaces a clean error while
    // the route guard redirects to login).
    const freshUser = await this.refreshOrSignOut();
    const retryHeaders = new Headers(init.headers ?? {});
    retryHeaders.set('Authorization', `Bearer ${freshUser.id_token!}`);
    const retryResponse = await fetch(url, {...init, headers: retryHeaders});

    if (retryResponse.status === 401) {
      await BaseApiClient.forceSignOut();
    }

    return retryResponse;
  }

  /**
   * Create fetch options with timeout controller
   */
  private createTimedFetchOptions(
    options: ApiRequestOptions,
    method: string
  ): RequestInit {
    const {timeoutMs, ...requestInit} = options;
    const fetchOptions: RequestInit = {
      ...requestInit,
      method,
      // Add timeout using AbortController
      signal: options.signal ?? new AbortController().signal,
    };

    // Explicit opt-out (e.g. Otto Views' bounded LLM generation) — no timer
    // at all, independent of whatever `.abort?.()` below does or doesn't do.
    if (timeoutMs === null) {
      return fetchOptions;
    }

    // Create timeout
    const timeoutId = setTimeout(
      () => {
        if (fetchOptions.signal instanceof AbortSignal) {
          const controller = fetchOptions.signal as AbortSignal & {
            abort?: () => void;
          };
          controller.abort?.();
        }
      },
      timeoutMs ?? (options.body ? API_TIMEOUTS.UPLOAD : API_TIMEOUTS.DEFAULT)
    );

    // Store timeout ID for cleanup
    (
      fetchOptions.signal as AbortSignal & {timeoutId?: NodeJS.Timeout}
    ).timeoutId = timeoutId;

    return fetchOptions;
  }

  /**
   * Process error response and throw appropriate error
   */
  private async handleBadResponseError(
    response: Response,
    options: RequestInit,
    endpoint: string
  ): Promise<void> {
    let errorData: unknown;
    let responseText = '';
    let responseTextError: unknown = null;

    try {
      responseText = await response.text();
    } catch (error) {
      responseText = 'Unable to read response body';
      responseTextError = error;
    }

    try {
      // Try to parse response as JSON first
      const parsedError: unknown = JSON.parse(responseText);
      errorData = parsedError;
    } catch {
      // Fallback: create error object with raw response text or status text
      errorData = {
        message: responseText?.trim()
          ? responseText.trim()
          : `${response.status} ${response.statusText ?? 'Error'}`.trim(),
      };
    }

    // Ensure errorData always has a message field
    // Check both `message` (standard) and `detail` (FastAPI format)
    const errorObj =
      typeof errorData === 'object' && errorData !== null ? errorData : {};
    const rawMessage =
      // ADR nested format: {"error": {"message": "..."}}
      ('error' in errorObj &&
      typeof (errorObj as {error?: {message?: string}}).error?.message ===
        'string'
        ? (errorObj as {error: {message: string}}).error.message.trim()
        : '') ||
      // Existing flat checks
      ('message' in errorObj &&
      typeof (errorObj as {message?: string}).message === 'string'
        ? (errorObj as {message: string}).message.trim()
        : '') ||
      ('detail' in errorObj &&
      typeof (errorObj as {detail?: string}).detail === 'string'
        ? (errorObj as {detail: string}).detail.trim()
        : '');
    const errorDataMessage =
      rawMessage || `${response.status} ${response.statusText ?? 'Error'}`;

    // Don't log here - let calling code decide whether to log
    // Some errors are expected (like 404 for optional resources)
    // and should be handled silently by the caller

    throw new EnhancedApiError(errorDataMessage, response.status, errorData);
  }

  /**
   * Handle unexpected errors in catch blocks
   */
  private handleUnexpectedError(
    error: unknown,
    endpoint: string,
    method: string
  ): never {
    if (error instanceof EnhancedApiError) {
      throw error;
    }

    const errorDetails = {
      endpoint,
      method,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    };

    logger.error('API request failed', errorDetails);

    // Enhanced error processing with classification
    const errorContext = createErrorContext(error, endpoint);

    // Create EnhancedApiError with proper classification
    if (errorContext.category === ErrorCategory.Network) {
      throw new EnhancedApiError(errorContext.userMessage, 0, error);
    }

    // For other errors, preserve original status if available
    const status = (error as {status?: number})?.status ?? 0;
    throw new EnhancedApiError(errorContext.userMessage, status, error);
  }

  /**
   * Check if a 401 response contains validation error details
   * (wrong credentials) rather than token expiration
   */
  private async isValidationError(response: Response): Promise<{
    isValidation: boolean;
    message?: string;
    code?: string;
  }> {
    try {
      const clone = response.clone();
      const contentType = clone.headers.get('content-type');

      if (!contentType?.includes('application/json')) {
        return {isValidation: false};
      }

      const data: unknown = await clone.json();

      // Check for validation_error code in error response
      const errorCode =
        (data as {error?: {code?: string}; code?: string}).error?.code ??
        (data as {code?: string}).code;
      const errorMessage =
        (
          data as {
            error?: {message?: string};
            detail?: string;
            message?: string;
          }
        ).error?.message ??
        (data as {detail?: string}).detail ??
        (data as {message?: string}).message;

      if (
        errorCode === 'validation_error' ||
        errorCode === 'invalid_credentials'
      ) {
        return {
          isValidation: true,
          message: errorMessage,
          code: errorCode as string | undefined,
        };
      }

      return {isValidation: false};
    } catch {
      // If we can't parse JSON, it's not a validation error
      return {isValidation: false};
    }
  }

  /**
   * Handle 401 response by actively refreshing the token via signinSilent()
   * and retrying the request once. If refresh or retry fails → force sign-out.
   */
  private async handle401AndRetry<T>(
    url: string,
    originalOptions: RequestInit,
    endpoint: string
  ): Promise<T> {
    try {
      const freshUser = await this.refreshOrSignOut();

      const retryHeaders = new Headers(originalOptions.headers ?? {});
      retryHeaders.set('Authorization', `Bearer ${freshUser.id_token!}`);

      const retryOptions = {...originalOptions, headers: retryHeaders};
      const retryResponse = await fetch(url, retryOptions);

      if (retryResponse.status === 401) {
        await BaseApiClient.forceSignOut();
        throw new EnhancedApiError(
          'Your session has been invalidated. Please sign in again.',
          401,
          {reason: 'still_unauthorized_after_refresh'}
        );
      }

      if (!retryResponse.ok) {
        await this.handleBadResponseError(
          retryResponse,
          retryOptions,
          endpoint
        );
      }

      if (retryResponse.status === 204) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {} as T;
      }

      return (await retryResponse.json()) as T;
    } catch (error) {
      if (error instanceof EnhancedApiError) {
        throw error;
      }

      await BaseApiClient.forceSignOut();
      throw new EnhancedApiError(
        'Failed to refresh authentication. Please sign in again.',
        401,
        error
      );
    }
  }

  /**
   * Handle 401 for blob requests — same pattern as handle401AndRetry.
   */
  private async handle401AndRetryBlob(
    url: string,
    originalOptions: RequestInit,
    endpoint: string
  ): Promise<Blob> {
    try {
      const freshUser = await this.refreshOrSignOut();

      const retryHeaders = new Headers(originalOptions.headers ?? {});
      retryHeaders.set('Authorization', `Bearer ${freshUser.id_token!}`);

      const retryResponse = await fetch(url, {
        ...originalOptions,
        headers: retryHeaders,
      });

      if (retryResponse.status === 401) {
        await BaseApiClient.forceSignOut();
        throw new EnhancedApiError(
          'Your session has been invalidated. Please sign in again.',
          401,
          {reason: 'still_unauthorized_after_refresh'}
        );
      }

      if (!retryResponse.ok) {
        await this.handleBadResponseError(
          retryResponse,
          originalOptions,
          endpoint
        );
      }

      return await retryResponse.blob();
    } catch (error) {
      if (error instanceof EnhancedApiError) {
        throw error;
      }

      await BaseApiClient.forceSignOut();
      throw new EnhancedApiError(
        'Failed to refresh authentication. Please sign in again.',
        401,
        error
      );
    }
  }

  /**
   * Attempt signinSilent(). If it fails or returns no user, force sign-out.
   */
  private async refreshOrSignOut() {
    const {refreshTokenSilent} = await import('@matic/lib/auth/user-manager');
    const freshUser = await refreshTokenSilent();

    if (!freshUser?.id_token) {
      await BaseApiClient.forceSignOut();
      throw new EnhancedApiError(
        'Authentication session expired. Please sign in again.',
        401,
        {reason: 'no_token_available'}
      );
    }

    return freshUser;
  }
}
